import { create } from 'zustand';

// ── Auth Store ────────────────────────────────────────────────────────────────
export const useAuthStore = create((set) => ({
  token: localStorage.getItem('cast_token') || null,
  user: JSON.parse(localStorage.getItem('cast_user') || 'null'),

  // Netflix-style viewer profiles: every account/token comes with the list of profiles on it
  // (see AuthResponseDto.Profiles), and activeProfile is whichever one the person picked via
  // the "Who's watching?" screen (POST /api/Profile/{id}/Select). A fresh login always clears
  // activeProfile so that screen shows again, even if one was previously selected.
  profiles: JSON.parse(localStorage.getItem('cast_profiles') || '[]'),
  activeProfile: JSON.parse(localStorage.getItem('cast_active_profile') || 'null'),

  hasSubscription: false,
  subscriptionExpiry: null,
  subscriptionPriceNaira: 2000,

  setAuth: (token, user) => {
    const profiles = user?.Profiles ?? [];
    localStorage.setItem('cast_token', token);
    localStorage.setItem('cast_user', JSON.stringify(user));
    localStorage.setItem('cast_profiles', JSON.stringify(profiles));
    localStorage.removeItem('cast_active_profile');
    set({ token, user, profiles, activeProfile: null, hasSubscription: user?.HasActiveSubscription ?? false });
  },

  setProfiles: (profiles) => {
    localStorage.setItem('cast_profiles', JSON.stringify(profiles));
    set({ profiles });
  },

  // Called after POST /api/Profile/{id}/Select succeeds: swaps in the profile-scoped token
  // (which now carries profileId/isKids) and records which profile is active.
  selectProfile: (token, profile) => {
    localStorage.setItem('cast_token', token);
    localStorage.setItem('cast_active_profile', JSON.stringify(profile));
    set({ token, activeProfile: profile });
  },

  clearActiveProfile: () => {
    localStorage.removeItem('cast_active_profile');
    set({ activeProfile: null });
  },

  logout: () => {
    localStorage.removeItem('cast_token');
    localStorage.removeItem('cast_user');
    localStorage.removeItem('cast_profiles');
    localStorage.removeItem('cast_active_profile');
    set({ token: null, user: null, profiles: [], activeProfile: null, hasSubscription: false, subscriptionExpiry: null });
  },

  setSubscription: ({ hasSubscription, expiryDate, subscriptionPriceNaira }) => {
    set({
      hasSubscription: !!hasSubscription,
      subscriptionExpiry: expiryDate ?? null,
      subscriptionPriceNaira: subscriptionPriceNaira ?? 2000,
    });
  },
}));

// ── Watch History (localStorage-persisted) ────────────────────────────────────
const loadHistory = () => {
  try { return JSON.parse(localStorage.getItem('cast_watch_history') || '{}'); }
  catch { return {}; }
};

export const useWatchStore = create((set, get) => ({
  history: loadHistory(),

  saveProgress: (episodeId, progress, duration, meta) => {
    const next = { ...get().history, [episodeId]: { progress, duration, updatedAt: Date.now(), ...meta } };
    localStorage.setItem('cast_watch_history', JSON.stringify(next));
    set({ history: next });
  },

  getProgress: (episodeId) => get().history[episodeId] || null,

  clearHistory: () => {
    localStorage.removeItem('cast_watch_history');
    set({ history: {} });
  },
}));

// ── UI Store ──────────────────────────────────────────────────────────────────
export const useUIStore = create((set) => ({
  navScrolled: false,
  setNavScrolled: (v) => set({ navScrolled: v }),

  searchOpen: false,
  toggleSearch: () => set((s) => ({ searchOpen: !s.searchOpen })),
}));
